import type { AuditFilters, EmailNotificationFilters, LabFilters, OrderFilters, PatientFilters, PaymentFilters, ProductFilters } from "@optimist/schemas"

/**
 * Query key factory for TanStack Query
 * Provides type-safe, consistent query keys across the application
 */
export const queryKeys = {
  stalledOrders: (hours: number, page: number) => ["dashboard", "stalled", hours, page] as const,
  dashboard: (page: number) => ["dashboard", page] as const,
  // Orders
  orders: {
    all: ['orders'] as const,
    lists: () => [...queryKeys.orders.all, 'list'] as const,
    list: (filters: OrderFilters) => [...queryKeys.orders.lists(), filters] as const,
    details: () => [...queryKeys.orders.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.orders.details(), id] as const,
    timeline: (id: string) => [...queryKeys.orders.detail(id), 'timeline'] as const,
  },

  // Patients
  patients: {
    all: ['patients'] as const,
    lists: () => [...queryKeys.patients.all, 'list'] as const,
    list: (filters: PatientFilters) =>
      [...queryKeys.patients.lists(), filters] as const,
    details: () => [...queryKeys.patients.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.patients.details(), id] as const,
  },

  // Labs
  labs: {
    all: ['labs'] as const,
    lists: () => [...queryKeys.labs.all, 'list'] as const,
    list: (filters: LabFilters) => [...queryKeys.labs.lists(), filters] as const,
    details: () => [...queryKeys.labs.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.labs.details(), id] as const,
  },

  // Payments
  payments: {
    all: ['payments'] as const,
    lists: () => [...queryKeys.payments.all, 'list'] as const,
    list: (filters: PaymentFilters) => [...queryKeys.payments.lists(), filters] as const,
    details: () => [...queryKeys.payments.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.payments.details(), id] as const,
  },

  // Audit
  audit: {
    all: ['audit'] as const,
    lists: () => [...queryKeys.audit.all, 'list'] as const,
    list: (filters: AuditFilters) => [...queryKeys.audit.lists(), filters] as const,
  },

  notifications: {
    all: ['notifications'] as const,
    lists: () => [...queryKeys.notifications.all, 'list'] as const,
    list: (filters: EmailNotificationFilters) => [...queryKeys.notifications.lists(), filters] as const,
  },

  // Prescriptions
  prescriptions: {
    all: ['prescriptions'] as const,
    lists: () => [...queryKeys.prescriptions.all, 'list'] as const,
    list: (filters: { status?: string; page?: number; limit?: number }) =>
      [...queryKeys.prescriptions.lists(), filters] as const,
  },

  // Refills
  refills: {
    all: ['refills'] as const,
    lists: () => [...queryKeys.refills.all, 'list'] as const,
    list: (filters: { status?: string; page?: number; limit?: number }) =>
      [...queryKeys.refills.lists(), filters] as const,
  },

  // Products
  products: {
    all: ['products'] as const,
    lists: () => [...queryKeys.products.all, 'list'] as const,
    list: (filters: ProductFilters) =>
      [...queryKeys.products.lists(), filters] as const,
    details: () => [...queryKeys.products.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.products.details(), id] as const,
    feeRules: () => [...queryKeys.products.all, 'fee-rules'] as const,
  },
  genHealth: {
    all: ['gen-health'] as const,
    products: () => [...queryKeys.genHealth.all, 'products'] as const,
  },
  staffNotificationRouting: {
    all: ['staff-notification-routing'] as const,
  },
} as const
